import { Outlet, redirect } from '@tanstack/react-router';
import { designStudioDashboardUrl, designStudioEditorIndexTarget } from '@/services/router/design-studio-href';
import { lazyRoute } from '@/services/router/prefetch';
import { LastWorkspaceResolver, LazyPage } from './-route-layouts';

// Design Studio's editor pages. `route-ownership.ts` claims these URLs for hydra and these routes
// render them, inside the editor's shell and layout (kept mounted while moving between pages).

const P = '/workspaces/:workspaceId/design-studio';
export const DesignStudioEmailEditorRoute = lazyRoute(
  `${P}/editor/:id`,
  () => import('@/pages/workspaces/design-studio/editor/index.page'),
);
export const DesignStudioWhatsappEditorRoute = lazyRoute(
  `${P}/editor/whatsapp/:id`,
  () => import('@/pages/workspaces/design-studio/editors/whatsapp/index.page'),
);
export const DesignStudioInboxMessageEditorRoute = lazyRoute(
  `${P}/editor/inbox-message/:id`,
  () => import('@/pages/workspaces/design-studio/editors/inbox-message/index.page'),
);
export const DesignStudioSmsEditorRoute = lazyRoute(
  `${P}/editor/sms/:id`,
  () => import('@/pages/workspaces/design-studio/editors/sms/index.page'),
);
export const DesignStudioPushEditorRoute = lazyRoute(
  `${P}/editor/push/:id`,
  () => import('@/pages/workspaces/design-studio/editors/push/index.page'),
);
export const DesignStudioSignUpFormEditorRoute = lazyRoute(
  `${P}/editor/sign-up-form/:id`,
  () => import('@/pages/workspaces/design-studio/editors/sign-up-form/index.page'),
);
export const DesignStudioInAppEditorRoute = lazyRoute(
  `${P}/editor/in-app/:id`,
  () => import('@/pages/workspaces/design-studio/editors/in-app/index.page'),
);
export const DesignStudioInboxPreviewsRoute = lazyRoute(
  `${P}/editor/:id/inbox-previews/:runId`,
  () => import('@/pages/workspaces/design-studio/inbox-previews/index.page'),
);
export const DesignStudioInboxPreviewRoute = lazyRoute(
  `${P}/editor/:id/inbox-previews/:runId/previews/:previewId`,
  () => import('@/pages/workspaces/design-studio/inbox-previews/preview/index.page'),
);
export const DesignStudioJourneysTemplateRoute = lazyRoute(
  `${P}/journeys/:templateWorkspaceId/:templateId`,
  () => import('@/pages/workspaces/design-studio/journeys/[workspaceId]/[templateId]/index.page'),
);
export const DesignStudioBrandRoute = lazyRoute(
  `${P}/brand`,
  () => import('@/pages/workspaces/design-studio/brand/index.page'),
);
export const DesignStudioAssetsRoute = lazyRoute(
  `${P}/assets`,
  () => import('@/pages/workspaces/design-studio/assets/index.page'),
);

// Lazy so the editor's shell, stores and providers stay out of the entry chunk.
const EditorLayout = lazyRoute(`${P}/editor`, () => import('@/features/design-studio/editor/editor-layout'));

/** The layout around the workspace's Design Studio editor pages. */
export function DesignStudioEditorLayoutRoute() {
  return (
    <LastWorkspaceResolver>
      <LazyPage>
        <EditorLayout>
          <LazyPage>
            <Outlet />
          </LazyPage>
        </EditorLayout>
      </LazyPage>
    </LastWorkspaceResolver>
  );
}

type WorkspaceRouteContext = { params: { workspaceId: string } };

/** `beforeLoad` for `/design-studio/welcome`, which only leads to the dashboard. */
export function redirectToDesignStudioDashboard({ params }: WorkspaceRouteContext) {
  throw redirect({ href: designStudioDashboardUrl(params.workspaceId), replace: true });
}

/** `beforeLoad` for `/design-studio/editor`: `?file=<id>` opens that file; without it, the dashboard. */
export function redirectFromEditorIndex({
  params,
  location,
}: WorkspaceRouteContext & { location: { searchStr: string } }) {
  throw redirect({ href: designStudioEditorIndexTarget(params.workspaceId, location.searchStr), replace: true });
}
